// Types
import { memo, useCallback, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
// Slices
import {
  checkDisable,
  createDynamicAvailability,
  deleteDynamicAvailability,
  duplicateDynamicAvailability,
  dynamicAvailabilityLoading,
  dynamicAvailabilityMapSelector,
  dynamicAvailabilitySelector,
  getDrawer,
  selectedDynamicAvailabilityIdsSelector,
  setFirstDynamicAvailability as setFirstDynamicAvailabilityAction,
  setSecondDynamicAvailability as setSecondDynamicAvailabilityAction,
} from 'slices/dynamicAvailability.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  EDynamicAvailabilityType,
  type TDynamicAvailability,
} from '@timeedit/types';
// Components
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';
import { Dropdown, type MenuProps, Space } from '@timeedit/ui-components/antd';
// Utils
import intl from '../../../i18n/intl';
// Styles
import './DynamicAvailabilityList.scss';

const language: any = intl.messages;

type TDataItem = {
  id: number;
  name: string;
  type: string;
  dynamicAvailabilityType: EDynamicAvailabilityType;
};

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const DynamicAvailabilityList = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const dynamicAvailabilities = useAppSelector(dynamicAvailabilitySelector);
  const dynamicAvailabilitiesMap = useAppSelector(
    dynamicAvailabilityMapSelector
  );
  const loading = useAppSelector(dynamicAvailabilityLoading);
  const selectedIds = useAppSelector(selectedDynamicAvailabilityIdsSelector);

  const [confirmDelete, delContextHolder] = useDeleteConfirmation();

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const updateFilterSettings = useCallback((settings: TEIFilter) => {
    setFilterSettings(settings);
  }, []);

  const onDuplicate = useCallback(
    (dataItem: TDataItem) => {
      const dynamicAvailability = dynamicAvailabilitiesMap[dataItem.id];
      if (dynamicAvailability)
        dispatch(duplicateDynamicAvailability(dynamicAvailability));
    },
    [dispatch, dynamicAvailabilitiesMap]
  );
  const onCreate = useCallback(
    (dynamicAvailabilityType: EDynamicAvailabilityType) => {
      dispatch(createDynamicAvailability(dynamicAvailabilityType));
    },
    [dispatch]
  );

  const setDynamicAvailability = useCallback(
    (
      isFirstDrawer: boolean,
      dynamicAvailability?: TDynamicAvailability,
      checkChanged = true
    ) => {
      const isFirstDynamicAvailabilitySaveDisabled = dispatch(
        checkDisable(true)
      );
      const isSecondDynamicAvailabilitySaveDisabled = dispatch(
        checkDisable(false)
      );
      if (checkChanged) {
        if (isFirstDrawer && !isFirstDynamicAvailabilitySaveDisabled) {
          return confirmDiscardPaddingChanges(true, () =>
            dispatch(setFirstDynamicAvailabilityAction(dynamicAvailability))
          );
        }
        if (!isFirstDrawer && !isSecondDynamicAvailabilitySaveDisabled) {
          return confirmDiscardPaddingChanges(false, () =>
            dispatch(setSecondDynamicAvailabilityAction(dynamicAvailability))
          );
        }
      }
      dispatch(
        isFirstDrawer
          ? setFirstDynamicAvailabilityAction(dynamicAvailability)
          : setSecondDynamicAvailabilityAction(dynamicAvailability)
      );
    },
    [confirmDiscardPaddingChanges, dispatch]
  );

  const onRow = useRowProps<TDataItem>(({ id }: TDataItem) => {
    const dynamicAvailability = dynamicAvailabilitiesMap[id];
    setDynamicAvailability(true, dynamicAvailability);
  }, selectedIds);

  const onCompare = useCallback(
    (dataItem: TDataItem) => {
      const firstDrawerData = dispatch(getDrawer(true));
      const dynamicAvailability = dynamicAvailabilitiesMap[dataItem.id];
      if (!firstDrawerData) {
        setDynamicAvailability(true, dynamicAvailability);
      } else {
        setDynamicAvailability(false, dynamicAvailability);
      }
    },
    [dispatch, dynamicAvailabilitiesMap, setDynamicAvailability]
  );

  const onDelete = useCallback(
    (dataItem: TDataItem) => {
      confirmDelete(dataItem?.name, () => {
        dispatch(deleteDynamicAvailability(dataItem.id));
      });
    },
    [confirmDelete, dispatch]
  );

  const dataSource: TDataItem[] = useMemo(
    () =>
      dynamicAvailabilities
        .map((item) => {
          return {
            id: item.id,
            name: item.name || 'New dynamic availability',
            type: language[item.dynamicAvailabilityType],
            dynamicAvailabilityType: item.dynamicAvailabilityType,
          };
        })
        .filter((da) => {
          let matchedType = true;
          let matchedText = true;
          const {
            filters: { type },
            freeTextSearch,
          } = filterSettings;
          if (type) {
            matchedType = da.dynamicAvailabilityType === type;
          }
          if (freeTextSearch) {
            matchedText = new RegExp(freeTextSearch, 'i').test(da.name);
          }
          return matchedType && matchedText;
        }),
    [dynamicAvailabilities, filterSettings]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(getDrawer(true))),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: TDataItem) =>
      getMenuProps<TDataItem>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const createNewMenu: MenuProps = useMemo(
    () => ({
      items: [
        {
          key: EDynamicAvailabilityType.FREE_TIME,
          label: language[EDynamicAvailabilityType.FREE_TIME],
        },
        {
          key: EDynamicAvailabilityType.TOTAL_HOURS,
          label: language[EDynamicAvailabilityType.TOTAL_HOURS],
        },
        {
          key: EDynamicAvailabilityType.CONSECUTIVE_HOURS,
          label: language[EDynamicAvailabilityType.CONSECUTIVE_HOURS],
        },
        {
          key: EDynamicAvailabilityType.TOTAL_RESERVATIONS,
          label: language[EDynamicAvailabilityType.TOTAL_RESERVATIONS],
        },
      ],
      onClick: (info) => {
        onCreate(info.key as EDynamicAvailabilityType);
      },
    }),
    [onCreate]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Dropdown menu={createNewMenu} trigger={['click']}>
          <Button size="small" type="primary" onClick={() => {}}>
            Create new
          </Button>
        </Dropdown>
      </Space>
    ),
    [createNewMenu]
  );

  const title = useTableTitle<TDataItem>({
    total: dynamicAvailabilities.length,
    filteredAmount: dataSource.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="dynamic-availability-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        settings={filterSettings}
        onChange={updateFilterSettings}
        options={options}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={dataSource}
          columns={columns}
          footer={footer}
          getMenuProps={menuProps}
          onRow={onRow}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {delContextHolder}
    </div>
  );
};

export default memo(DynamicAvailabilityList);

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
    sorter: (a: TDataItem, b: TDataItem) => a.name.localeCompare(b.name),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    ellipsis: true,
    sorter: (a: TDataItem, b: TDataItem) => a.type.localeCompare(b.type),
  },
];

const defaultFields: TEIFilterField[] = [
  {
    key: 'type',
    label: 'Type',
    type: TEEFilterType.text,
  },
];
const options = {
  type: Object.values(EDynamicAvailabilityType)
    .map((type) => ({
      value: type,
      label: language[type],
    }))
    .sort((a, b) => {
      if (!a.label || !b.label) return 1;
      return a.label.localeCompare(b.label);
    }),
};
